import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { t } from '@lingui/core/macro';
import { BrandInsightsPeriod } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBadge, NoddiCard, NoddiSegmentedControl, useNoddiQueryParam } from 'noddi-ui-web';
import { useParams } from 'react-router-dom';
import { createEnumParam, withDefault } from 'use-query-params';

import { getTabsTranslations } from '../../utils/commonTranslations';

import { BrandInsightsHero } from './BrandInsightsHero';
import { BrandInsightsLiveFeed } from './BrandInsightsLiveFeed';
import { BrandInsightsPartners } from './BrandInsightsPartners';
import { BrandInsightsServices } from './BrandInsightsServices';
import { BrandInsightsTrend } from './BrandInsightsTrend';
import { formatDateRange, getComparisonLabel, getComparisonSeriesLabel, getPeriodOptions } from './brandInsightsFormat';
import { usePartnerMonthParam } from './usePartnerMonthParam';

// The live feed and "today so far" refresh on their own; the backend caches
// the live endpoint for 30 seconds, so polling faster gains nothing.
const LIVE_REFRESH_MS = 30_000;

const PeriodParam = withDefault(
  createEnumParam<BrandInsightsPeriod>(Object.values(BrandInsightsPeriod)),
  BrandInsightsPeriod.LAST_30_DAYS
);

/** Read-only booking figures of one brand, opened from a shared link without login. */
export const BrandInsightsPage = () => {
  const { insightsToken } = useParams();
  const token = insightsToken!;
  const [period, setPeriod] = useNoddiQueryParam('period', PeriodParam);
  const [, setPartnerMonth] = usePartnerMonthParam();

  // A period without cached figures shows skeletons in the sections it drives; the header,
  // the live card and the period picker stay mounted.
  const { data: insights, error } = noddiAsync.useGet({
    type: URLKeys.getBrandInsights,
    input: { insightsToken: token, period }
  });

  const {
    data: live,
    error: liveError,
    isRefetching: isLiveRefetching,
    refetch: refetchLive
  } = noddiAsync.useGet({
    type: URLKeys.getBrandInsightsLive,
    input: { insightsToken: token },
    queryConfig: { refetchInterval: LIVE_REFRESH_MS }
  });

  if (error) {
    return <ContentWrapperCustomerApp width='wide' hideBackButton error={error} />;
  }

  const periodOptions = getPeriodOptions();
  const periodLabel = periodOptions.find((option) => option.value === period)?.label ?? '';

  // A month picked in the partner table narrows the period it was picked in; a new period starts whole.
  const changePeriod = (next: BrandInsightsPeriod) => {
    setPeriod(next);
    setPartnerMonth(undefined);
  };

  return (
    <ContentWrapperCustomerApp width='wide' hideBackButton>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-wrap items-center gap-4'>
          <h1 className='text-xl font-semibold'>{t`Marketplace insights`}</h1>
          <NoddiBadge variant='outline'>{t`Read-only link`}</NoddiBadge>
        </div>

        {/* Two zones: live figures first, outside the period's reach; the period picker then heads
            everything below it. */}
        <BrandInsightsLiveFeed
          live={live}
          error={liveError}
          isRetrying={isLiveRefetching}
          onRetry={() => void refetchLive()}
        />

        <div className='flex flex-col gap-6'>
          <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2'>
            <NoddiSegmentedControl
              ariaLabel={t`Period`}
              value={period}
              onValueChange={changePeriod}
              options={periodOptions}
              translations={getTabsTranslations()}
            />
            {insights && (
              <span className='text-sm text-muted-foreground'>
                {formatDateRange(insights.fromDate, insights.toDate)}
              </span>
            )}
          </div>

          {/* Readers took the figures for service dates; say plainly what is counted and when. */}
          <ul className='flex list-disc flex-col gap-1 pl-5 text-sm text-muted-foreground'>
            <li>{t`Counts bookings customers made on this marketplace.`}</li>
            <li>{t`Each booking counts on the day the customer booked, not the day of the service.`}</li>
            <li>{t`Canceled bookings are not included. Changes compare with the same period last year.`}</li>
          </ul>

          <NoddiCard className='bg-linear-to-br from-primary/5 via-card to-card' contentClassName='flex flex-col gap-6'>
            <BrandInsightsHero insights={insights} comparisonLabel={getComparisonLabel()} />
            <BrandInsightsTrend insights={insights} comparisonLabel={getComparisonSeriesLabel()} />
          </NoddiCard>

          <BrandInsightsServices services={insights?.services} />

          <BrandInsightsPartners
            insightsToken={token}
            periodPartners={insights?.partners}
            periodTotals={insights?.totals}
            periodLabel={periodLabel}
          />
        </div>

        <p className='text-xs text-muted-foreground'>
          {t`Shared by Navio. Amounts incl. VAT. No customer data is shown.`}
        </p>
      </div>
    </ContentWrapperCustomerApp>
  );
};
